// @flow

import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { toastr } from 'react-redux-toastr';
import useCancelGrossRentCertificationMutation from './hooks.useCancelGrossRentCertificationMutation';
import usePermissionsHook from '../../../components/ElementWithPermissions/usePermissionsHook';
import useIntl from '../../../hooks/useIntl';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import { extractErrorMessage } from '../../../utils/errors';
import generalMessages from '../../App/messages';
import messages from './messages';
import type { CancelGrossRentCertificationModalCert } from './types';

/**
 * Owns the confirmation modal state and the cancel mutation's side effects for one Gross Rent
 * Certification row, so the table only has to render the menu option and the modal.
 *
 * Cancelling soft deletes the certification, so three reads go stale: this table, the household
 * qualifications the resident profile's open Gross Rent banner is derived from, and the eligible
 * effective dates the profile's Start Certification dropdown offers.
 */
const CANCEL_CERT_SCOPE = ['manage-certifications-gr-cert-cancel'];

const useCancelGrossRentCertificationAction = () => {
  const [certToCancel, setCertToCancel] =
    useState<?CancelGrossRentCertificationModalCert>(null);
  // Sync guard: `isSubmitting` and the Confirm button's disabled state come from the same render,
  // so two clicks inside one tick would both reach `mutate`. The second request 404s on the
  // already deleted row and the user gets a success and an error toast together.
  const inFlightRef = useRef(false);

  const hasCancelPermission = usePermissionsHook({ scope: CANCEL_CERT_SCOPE });
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const queryClient = useQueryClient();

  const cancelMutation = useCancelGrossRentCertificationMutation({
    organizationId,
    propertyId,
    options: {
      onSettled: () => {
        inFlightRef.current = false;
      },
      onSuccess: () => {
        setCertToCancel(null);
        queryClient.invalidateQueries([
          'grossRentCertifications',
          organizationId,
          propertyId,
        ]);
        // The resident profile's open Gross Rent banner is derived from this read, and the
        // soft-deleted cert drops out of it. Scoped to this org and property; the rest of the key
        // is householdId and propertyAffordableProgramId, neither of which the row carries.
        queryClient.invalidateQueries([
          'getHouseholdProfileAffordableQualificationsByProgram',
          organizationId,
          propertyId,
        ]);
        // The canceled effective date becomes eligible to start again, so the resident
        // profile's Effective Date dropdown has to be rebuilt. Keyed by prefix because the row
        // does not carry the household id.
        queryClient.invalidateQueries([
          'eligibleGrossRentEffectiveDates',
          organizationId,
          propertyId,
        ]);
        toastr.success(
          intl.formatMessage(generalMessages.success),
          intl.formatMessage(messages.cancelCertificationSuccess),
        );
      },
      onError: (error: mixed) => {
        toastr.error(
          intl.formatMessage(generalMessages.error),
          intl.formatMessage(messages.errorCancellingCertification, {
            errorMessage: extractErrorMessage(error),
          }),
        );
      },
    },
  });

  const isSubmitting = cancelMutation.isLoading;

  const openCancelModal = useCallback(
    (cert: CancelGrossRentCertificationModalCert) => setCertToCancel(cert),
    [],
  );
  const closeCancelModal = useCallback(() => setCertToCancel(null), []);
  const confirmCancel = useCallback(() => {
    if (inFlightRef.current || !certToCancel?.affordableQualificationId) {
      return;
    }
    inFlightRef.current = true;
    cancelMutation.mutate({
      affordableQualificationId: certToCancel.affordableQualificationId,
    });
  }, [cancelMutation, certToCancel]);

  return {
    certToCancel,
    hasCancelPermission,
    isCancelModalOpen: Boolean(certToCancel),
    isSubmitting,
    openCancelModal,
    closeCancelModal,
    confirmCancel,
  };
};

export default useCancelGrossRentCertificationAction;
